import { orderBy } from "lodash";

import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";

import {
  BREAKPOINT_COLS,
  BREAKPOINT_WIDTH_XS,
  BREAKPOINT_WIDTH_XXS,
} from "./constants";
import {
  type BreakpointName,
  type Layouts,
  type Layout,
  type TileDescriptor,
  INITIAL_LAYOUTS,
} from "./types";

export function getBreakpointFromWidth(width: number): BreakpointName {
  const breakpoints: { width: number; name: BreakpointName }[] = [
    { width: BREAKPOINT_WIDTH_XS, name: "xs" },
    { width: BREAKPOINT_WIDTH_XXS, name: "xxs" },
  ];

  for (const breakpoint of breakpoints) {
    if (width / breakpoint.width > 1) {
      return breakpoint.name;
    }
  }

  return "xxs";
}

export function getColsFromBreakpoint(breakpoint: BreakpointName) {
  return BREAKPOINT_COLS[breakpoint];
}

export function createEmptyLayouts(): Layouts {
  return {
    xs: { x: 0, y: 0, width: 0, height: 0 },
    xxs: { x: 0, y: 0, width: 0, height: 0 },
  };
}

export function findEmptySlot(
  type: "first" | "last",
  {
    layouts,
    cols,
    width,
    height,
  }: {
    layouts: Layout[];
    cols: number;
    width: number;
    height: number;
  },
) {
  let rows = 1;

  for (const tile of layouts) {
    if (tile.y + tile.height > rows) {
      rows = tile.y + tile.height;
    }
  }

  rows += height;

  const board = [];

  for (let i = 0; i < rows; i++) {
    board.push(Array(cols).fill(0));
  }

  for (const tile of layouts) {
    const yStart = tile.y;
    const yEnd = tile.y + tile.height - 1;

    const xStart = tile.x;
    const xEnd = tile.x + tile.width - 1;

    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board[i].length; j++) {
        if (i >= yStart && i <= yEnd && j >= xStart && j <= xEnd) {
          board[i][j] = 1;
        }
      }
    }
  }

  let x = 0;
  let y = 0;

  let found = false;

  let startI = 0;

  if (type === "last") {
    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board[i].length; j++) {
        if (board[i][j] === 1) {
          startI = i;
        }
      }
    }

    if (startI > 0) {
      startI -= 1;
    }
  }

  for (let i = startI; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      let isAvailable = true;

      for (let k = i; k < i + height; k++) {
        for (let l = j; l < j + width; l++) {
          if (board[k][l] !== 0) {
            isAvailable = false;
          }
        }
      }

      if (isAvailable) {
        y = i;
        x = j;
        found = true;
        break;
      }
    }

    if (found) {
      break;
    }
  }

  return [x, y] as const;
}

export const initializeTileLayouts = ({
  dimensions,
  tiles,
}: {
  dimensions: (bp: BreakpointName) => Pick<Layout, "height" | "width">;
  tiles: TileDescriptor[];
}) => {
  const layouts = { ...INITIAL_LAYOUTS };

  let breakpoint: BreakpointName;
  for (breakpoint in BREAKPOINT_COLS) {
    const [x, y] = findEmptySlot("last", {
      ...dimensions(breakpoint),
      /* eslint-disable-next-line no-loop-func */
      layouts: tiles.map((t) => t.layouts[breakpoint]),
      cols: BREAKPOINT_COLS[breakpoint],
    });
    layouts[breakpoint] = { ...dimensions(breakpoint), x, y };
  }
  return layouts;
};

const FREQUENTLY_USED_METRICS_IDS = [
  "spend",
  "rev",
  "txns",
  "visits",
  "roas",
  "cac",
  "aov",
  "cpm",
  "ctr",
  "ecpc",
];

const CONVERSION_LAG_METRICS_IDS = [
  "rev",
  "revFt",
  "revRtn",
  "txns",
  "txnsFt",
  "txnsRtn",
  "roas",
  "roasFt",
  "roasRtn",
  "cac",
  "cacFt",
  "cacRtn",
  "revenueWebShops",
  "revenueTikTokShops",
  "revenueMetaShops",
  "transactionsWebShops",
  "transactionsTikTokShops",
  "transactionsMetaShops",
  "orderDiscounts",
  "orderDiscountsFt",
  "orderDiscountsRtn",
  "taxValue",
  "taxValueFt",
  "taxValueRtn",
  "shippingValue",
  "shippingValueFt",
  "shippingValueRtn",
  "orderRefundedValue",
  "orderRefundedValueFt",
  "orderRefundedValueRtn",
  "revenueOrderSubscription",
  "revenueOrderSubscriptionFirstTime",
  "revenueOrderSubscriptionRecurring",
  "revenueOrderSubscriptionOther",
  "transactionsOrderSubscription",
  "transactionsOrderSubscriptionFirstTime",
  "transactionsOrderSubscriptionRecurring",
  "transactionsOrderSubscriptionOther",
  "cacOrderSubscription",
  "cacOrderSubscriptionFirstTime",
  "cacOrderSubscriptionRecurring",
  "cacOrderSubscriptionOther",
  "orderDiscountsOrderSubscriptions",
  "orderDiscountsOrderSubscriptionsFt",
  "orderDiscountsOrderSubscriptionsRecurring",
  "orderDiscountsOrderSubscriptionsOther",
  "taxValueOrderSubscriptions",
  "taxValueOrderSubscriptionsFt",
  "taxValueOrderSubscriptionsRecurring",
  "taxValueOrderSubscriptionsOther",
  "shippingValueOrderSubscriptions",
  "shippingValueOrderSubscriptionsFt",
  "shippingValueOrderSubscriptionsRecurring",
  "shippingValueOrderSubscriptionsOther",
  "orderRefundedValueOrderSubscriptions",
  "orderRefundedValueOrderSubscriptionsFt",
  "orderRefundedValueOrderSubscriptionsRecurring",
  "orderRefundedValueOrderSubscriptionsOther",
];

export const getFrequentlyUsedMetrics = (metrics: SalesPageMetric[]) =>
  orderBy(
    metrics.filter((metric) => FREQUENTLY_USED_METRICS_IDS.includes(metric.id)),
    (metric) => FREQUENTLY_USED_METRICS_IDS.indexOf(metric.id),
  ) as SalesPageMetric[];

export const getFrequentlyUsedMetricsV2 = (metrics: SalesMetricV2[]) =>
  orderBy(
    metrics.filter((metric) => FREQUENTLY_USED_METRICS_IDS.includes(metric.id)),
    (metric) => FREQUENTLY_USED_METRICS_IDS.indexOf(metric.id),
  ) as SalesMetricV2[];

export const getConversionLagMetrics = (metrics: SalesPageMetric[]) =>
  orderBy(
    metrics.filter(
      (metric) =>
        CONVERSION_LAG_METRICS_IDS.includes(metric.id) || metric.baseMetrics,
    ),
    (metric) =>
      metric.baseMetrics ? 999 : CONVERSION_LAG_METRICS_IDS.indexOf(metric.id),
  ) as SalesPageMetric[];

export const isConversionLagMetric = (metric?: SalesPageMetric) =>
  !metric
    ? false
    : CONVERSION_LAG_METRICS_IDS.includes(metric.id) || !!metric.baseMetrics;

export const getExcludedFrequentlyUsedMetrics = (metrics: SalesPageMetric[]) =>
  metrics.filter((metric) => !FREQUENTLY_USED_METRICS_IDS.includes(metric.id));

export const getExcludedFrequentlyUsedMetricsV2 = (metrics: SalesMetricV2[]) =>
  metrics.filter((metric) => !FREQUENTLY_USED_METRICS_IDS.includes(metric.id));
